import { useState, useCallback, useEffect } from "react";
import {
  searchYouTubeChannels,
  getChannelDetailsById,
} from "../services/outreach/integrations/youtubeSearch";
import type {
  YouTubeSearchResult,
  YouTubeChannelSearchDetails,
  YouTubeSortOrder,
} from "../services/outreach/types";

// Loading state type for tracking different operations
type LoadingOperation = 'initial' | 'pagination' | 'sort' | 'none';

interface LoadingState {
  isLoading: boolean;
  operation: LoadingOperation;
  page?: number;
}

// Error logging utility for pagination and search errors
function logErrorWithContext(
  errorName: string,
  error: unknown,
  context: {
    query?: string;
    pageToken?: string;
    currentPage?: number;
    sortOrder?: string;
    resultsPerPage?: number;
    searchType?: 'search' | 'nextPage' | 'prevPage' | 'goToPage' | 'fetchDetails';
  }
) {
  const timestamp = new Date().toISOString();
  const errorMessage = error instanceof Error ? error.message : String(error);
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown';
  
  const logData = {
    timestamp,
    errorName,
    errorMessage,
    context,
    userAgent,
    url: typeof window !== 'undefined' ? window.location.href : 'unknown',
  };
  
  console.error(
    `[YouTube Search Error - ${errorName}]`,
    JSON.stringify(logData, null, 2),
    error
  );
}

interface UseYouTubeSearchReturn {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchResults: YouTubeSearchResult[];
  selectedChannel: YouTubeChannelSearchDetails | null;
  setSelectedChannel: (channel: YouTubeChannelSearchDetails | null) => void;
  loadingState: LoadingState;
  loading: boolean;
  detailsLoading: boolean;
  error: string | null;
  pageToken: string | undefined;
  prevPageToken: string | undefined;
  totalResults: number;
  performSearch: (overrideQuery?: string) => Promise<void>;
  loadNextPage: () => Promise<void>;
  loadPrevPage: () => Promise<void>;
  fetchChannelDetails: (channelId: string) => Promise<void>;
  resetSearch: () => void;
  // Pagination state
  currentPage: number;
  pageTokens: Map<number, string>;
  sortOrder: YouTubeSortOrder;
  resultsPerPage: number;
  resultsCache: Map<string, CachedResults>;
  // Pagination control functions
  nextPage: () => Promise<void>;
  prevPage: () => Promise<void>;
  goToPage: (pageNum: number) => Promise<void>;
  setSortOrder: (order: YouTubeSortOrder) => void;
  setResultsPerPage: (count: number) => void;
  // Retry functionality
  retryCount: number;
  canRetry: boolean;
  performRetry: () => Promise<void>;
}

// Helper function to generate cache key based on query, sort order, and page
function generateCacheKey(
  query: string,
  sortOrder: YouTubeSortOrder,
  page: number
): string {
  return `${query}:${sortOrder}:${page}`;
}

interface CachedResults {
  items: YouTubeSearchResult[];
  nextPageToken?: string;
  prevPageToken?: string;
}

const SESSION_STORAGE_KEY = "youtubeSearchState";

interface SerializedSearchState {
  query: string;
  sortOrder: YouTubeSortOrder;
  currentPage: number;
  cache: Record<string, CachedResults>;
}

// Helper function to serialize cache Map to JSON-compatible format
function serializeCache(cache: Map<string, CachedResults>): Record<string, CachedResults> {
  const result: Record<string, CachedResults> = {};
  cache.forEach((value, key) => {
    result[key] = value;
  });
  return result;
}

// Helper function to deserialize JSON back to Map
function deserializeCache(
  serialized: Record<string, CachedResults> | undefined
): Map<string, CachedResults> {
  const cache = new Map<string, CachedResults>();
  if (serialized) {
    Object.entries(serialized).forEach(([key, value]) => {
      cache.set(key, value);
    });
  }
  return cache;
}

function normalizeError(err: unknown): string {
  if (err && typeof err === "object" && "message" in err) {
    const message = String((err as { message?: string }).message ?? "");

    // API quota exceeded errors (403, 429 rate limit)
    if (message.toLowerCase().includes("quota") || message.includes("403") || message.includes("429")) {
      return "YouTube API quota exceeded. Please try again later.";
    }

    // Network connectivity errors
    if (message.toLowerCase().includes("network") || message.toLowerCase().includes("fetch")) {
      return "Network error. Please check your connection and try again.";
    }

    // Authorization and authentication errors (401, 403)
    if (
      message.toLowerCase().includes("unauthorized") ||
      message.includes("401") ||
      message.includes("403")
    ) {
      return "YouTube authorization failed. Please refresh and try again or contact support.";
    }

    // Invalid page token errors
    if (message.toLowerCase().includes("invalid page token") || 
        (message.toLowerCase().includes("token") && message.toLowerCase().includes("invalid"))) {
      return "Invalid page token. Returning to first page. Please try again.";
    }

    // General token retrieval errors
    if (message.toLowerCase().includes("token")) {
      return "Unable to retrieve YouTube access token. Please contact support.";
    }

    // API configuration errors
    if (message.toLowerCase().includes("key")) {
      return "YouTube API configuration error. Please contact support.";
    }

    // Timeout errors
    if (message.toLowerCase().includes("timeout") || message.toLowerCase().includes("timed out")) {
      return "Request timed out. Please try again.";
    }

    // Generic API errors with status codes
    if (message.includes("API error:")) {
      return `YouTube API error. Please try again or contact support if the problem persists.`;
    }
  }

  return "An error occurred while searching. Please try again.";
}

// Determine if an error is transient (retryable) or permanent
function isTransientError(err: unknown): boolean {
  if (err && typeof err === "object" && "message" in err) {
    const message = String((err as { message?: string }).message ?? "").toLowerCase();
    
    // Network and timeout errors are transient
    if (message.includes("network") || message.includes("fetch") || 
        message.includes("timeout") || message.includes("timed out")) {
      return true;
    }
    
    // Rate limiting (429) is transient
    if (message.includes("429") || message.includes("rate limited")) {
      return true;
    }
    
    // Temporary service errors are transient
    if (message.includes("503") || message.includes("502") || 
        message.includes("service unavailable") || message.includes("bad gateway")) {
      return true;
    }
  }
  
  return false;
}

// Client-side sorting function for YouTube search results
// Note: For 'relevance' and 'title', sorting is handled server-side via the API order parameter.
// For 'viewCount', we apply client-side sorting since the API handles it natively.
function applySortOrder(
  results: YouTubeSearchResult[],
  sortOrder: YouTubeSortOrder
): YouTubeSearchResult[] {
  if (sortOrder === "relevance") {
    // Don't sort - use API order
    return results;
  }

  const sorted = [...results];

  if (sortOrder === "viewCount") {
    // Sort by view count descending (high to low)
    // Note: YouTube API natively supports this, but we may apply client-side sorting
    // in cases where backfill has combined multiple API responses
    sorted.sort((a, b) => (b.viewCount ?? 0) - (a.viewCount ?? 0));
  } else if (sortOrder === "title") {
    // Sort alphabetically by title (A-Z)
    sorted.sort((a, b) =>
      a.channelTitle.localeCompare(b.channelTitle, undefined, {
        sensitivity: "base",
      })
    );
  }

  return sorted;
}

// Backfill helper: Fetches additional pages when filtering reduces results below target
async function fetchWithBackfill(
  query: string,
  targetCount: number,
  initialPageToken?: string,
  sortOrder: YouTubeSortOrder = "relevance"
): Promise<{
  items: YouTubeSearchResult[];
  nextPageToken?: string;
  prevPageToken?: string;
  totalResults: number;
}> {
  const MAX_BACKFILL_ITERATIONS = 5; // Safety limit to prevent infinite loops
  const allItems: YouTubeSearchResult[] = [];
  let currentPageToken = initialPageToken;
  let lastNextPageToken: string | undefined;
  let lastPrevPageToken: string | undefined;
  let iteration = 0;

  while (allItems.length < targetCount && iteration < MAX_BACKFILL_ITERATIONS) {
    iteration++;

    try {
      const result = await searchYouTubeChannels({
        query,
        maxResults: targetCount, // Request full target amount each time
        pageToken: currentPageToken,
      });

      // Add new items to collection
      allItems.push(...result.items);
      
      // Store pagination tokens from the most recent fetch
      lastNextPageToken = result.nextPageToken;
      lastPrevPageToken = result.prevPageToken;

      // If we have enough items or no more pages, stop
      if (allItems.length >= targetCount || !result.nextPageToken) {
        break;
      }

      // Continue with next page
      currentPageToken = result.nextPageToken;
    } catch (error) {
      // If an error occurs during backfill, return what we have so far
      console.error('Error during pagination backfill:', error);
      break;
    }
  }

  // Trim to exact target count if we fetched more than needed
  const finalItems = allItems.slice(0, targetCount);

  return {
    items: finalItems,
    nextPageToken: lastNextPageToken,
    prevPageToken: lastPrevPageToken,
    totalResults: finalItems.length,
  };
}

export function useYouTubeSearch(): UseYouTubeSearchReturn {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchResults, setSearchResults] = useState<YouTubeSearchResult[]>([]);
  const [selectedChannel, setSelectedChannel] =
    useState<YouTubeChannelSearchDetails | null>(null);
  const [loadingState, setLoadingState] = useState<LoadingState>({
    isLoading: false,
    operation: 'none',
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [detailsLoading, setDetailsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pageToken, setPageToken] = useState<string | undefined>(undefined);
  const [prevPageToken, setPrevPageToken] = useState<string | undefined>(
    undefined
  );
  const [totalResults, setTotalResults] = useState<number>(0);

  // Pagination state variables
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageTokens, setPageTokens] = useState<Map<number, string>>(new Map());
  const [sortOrder, setSortOrder] = useState<YouTubeSortOrder>("relevance");
  const [resultsPerPage, setResultsPerPage] = useState<number>(20);

  // Results caching mechanism
  const [resultsCache, setResultsCache] = useState<
    Map<string, CachedResults>
  >(new Map());

  // Retry mechanism for transient errors
  const [retryCount, setRetryCount] = useState<number>(0);
  const [lastOperationType, setLastOperationType] = useState<string | null>(null);
  const [pendingRetry, setPendingRetry] = useState<boolean>(false);

  const MAX_RETRIES = 3;
  const RETRY_DELAY_MS = 2000;

  // Restore search state from sessionStorage on component mount
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as SerializedSearchState;
        setSearchQuery(parsed.query);
        setSortOrder(parsed.sortOrder);
        setCurrentPage(parsed.currentPage);
        setResultsCache(deserializeCache(parsed.cache));
      }
    } catch (err) {
      console.warn("Failed to restore search state from sessionStorage:", err);
      // Silently fail - just start with empty state
    }
  }, []);

  const performSearch = useCallback(
    async (overrideQuery?: string) => {
      const queryToUse = overrideQuery ?? searchQuery;
      const trimmedQuery = queryToUse.trim();
      if (!trimmedQuery) return;

      const cacheKey = generateCacheKey(trimmedQuery, sortOrder, 1);
      const cachedResult = resultsCache.get(cacheKey);

      // Return cached results if available
      if (cachedResult) {
        setSearchResults(cachedResult.items);
        setPageToken(cachedResult.nextPageToken);
        setPrevPageToken(cachedResult.prevPageToken);
        setSelectedChannel(null);
        setCurrentPage(1);
        setPageTokens(new Map());
        return;
      }

      setLoading(true);
      setLoadingState({ isLoading: true, operation: 'initial' });
      setError(null);

      try {
        // Use backfill mechanism to ensure we get the target number of results
        const result = await fetchWithBackfill(
          trimmedQuery,
          resultsPerPage,
          undefined,
          sortOrder
        );

        // Apply client-side sorting
        const sortedResults = applySortOrder(result.items, sortOrder);

        setSearchResults(sortedResults);
        setPageToken(result.nextPageToken);
        setPrevPageToken(result.prevPageToken);
        setTotalResults(result.totalResults ?? 0);
        setSelectedChannel(null);
        setCurrentPage(1);
        setPageTokens(new Map());

        // Cache the sorted results
        const newCache = new Map(resultsCache);
        newCache.set(cacheKey, {
          items: sortedResults,
          nextPageToken: result.nextPageToken,
          prevPageToken: result.prevPageToken,
        });
        setResultsCache(newCache);
      } catch (err) {
        logErrorWithContext('performSearch', err, {
          query: trimmedQuery,
          currentPage: 1,
          sortOrder,
          resultsPerPage,
          searchType: 'search',
        });
        setError(normalizeError(err));
        setSearchResults([]);
        setTotalResults(0);
      } finally {
        setLoading(false);
        setLoadingState({ isLoading: false, operation: 'none' });
      }
    },
    [searchQuery, sortOrder, resultsCache, resultsPerPage]
  );

  const loadNextPage = useCallback(async () => {
    if (!pageToken) return;

    const cacheKey = generateCacheKey(searchQuery, sortOrder, currentPage + 1);
    const cachedResult = resultsCache.get(cacheKey);

    // Return cached results if available
    if (cachedResult) {
      setSearchResults(cachedResult.items);
      setPageToken(cachedResult.nextPageToken);
      setPrevPageToken(cachedResult.prevPageToken);
      setSelectedChannel(null);

      // Store the current page token in the map before moving forward
      const newPageTokens = new Map(pageTokens);
      newPageTokens.set(currentPage, pageToken);
      setPageTokens(newPageTokens);
      setCurrentPage((prev) => prev + 1);
      return;
    }

    setLoading(true);
    setLoadingState({ isLoading: true, operation: 'pagination', page: currentPage + 1 });
    setError(null);

    try {
      // Use backfill mechanism to ensure we get the target number of results
      const result = await fetchWithBackfill(
        searchQuery,
        resultsPerPage,
        pageToken,
        sortOrder
      );

      // Apply client-side sorting
      const sortedResults = applySortOrder(result.items, sortOrder);

      setSearchResults(sortedResults);
      setPageToken(result.nextPageToken);
      setPrevPageToken(result.prevPageToken);
      setSelectedChannel(null);

      // Store the current page token in the map before moving forward
      const newPageTokens = new Map(pageTokens);
      newPageTokens.set(currentPage, pageToken);
      setPageTokens(newPageTokens);

      // Cache the sorted results
      const newCache = new Map(resultsCache);
      newCache.set(cacheKey, {
        items: sortedResults,
        nextPageToken: result.nextPageToken,
        prevPageToken: result.prevPageToken,
      });
      setResultsCache(newCache);

      setCurrentPage((prev) => prev + 1);
    } catch (err) {
      logErrorWithContext('loadNextPage', err, {
        query: searchQuery,
        pageToken,
        currentPage,
        sortOrder,
        resultsPerPage,
        searchType: 'nextPage',
      });
      setError(normalizeError(err));
    } finally {
      setLoading(false);
      setLoadingState({ isLoading: false, operation: 'none' });
    }
  }, [pageToken, searchQuery, resultsPerPage, pageTokens, currentPage, sortOrder, resultsCache]);

  const loadPrevPage = useCallback(async () => {
    if (!prevPageToken) return;

    const cacheKey = generateCacheKey(searchQuery, sortOrder, currentPage - 1);
    const cachedResult = resultsCache.get(cacheKey);

    // Return cached results if available
    if (cachedResult) {
      setSearchResults(cachedResult.items);
      setPageToken(cachedResult.nextPageToken);
      setPrevPageToken(cachedResult.prevPageToken);
      setSelectedChannel(null);
      setCurrentPage((prev) => Math.max(1, prev - 1));
      return;
    }

    setLoading(true);
    setLoadingState({ isLoading: true, operation: 'pagination', page: currentPage - 1 });
    setError(null);

    try {
      // Use backfill mechanism to ensure we get the target number of results
      const result = await fetchWithBackfill(
        searchQuery,
        resultsPerPage,
        prevPageToken,
        sortOrder
      );

      // Apply client-side sorting
      const sortedResults = applySortOrder(result.items, sortOrder);

      setSearchResults(sortedResults);
      setPageToken(result.nextPageToken);
      setPrevPageToken(result.prevPageToken);
      setSelectedChannel(null);

      // Cache the sorted results
      const newCache = new Map(resultsCache);
      newCache.set(cacheKey, {
        items: sortedResults,
        nextPageToken: result.nextPageToken,
        prevPageToken: result.prevPageToken,
      });
      setResultsCache(newCache);

      setCurrentPage((prev) => Math.max(1, prev - 1));
    } catch (err) {
      logErrorWithContext('loadPrevPage', err, {
        query: searchQuery,
        pageToken: prevPageToken,
        currentPage,
        sortOrder,
        resultsPerPage,
        searchType: 'prevPage',
      });
      setError(normalizeError(err));
    } finally {
      setLoading(false);
      setLoadingState({ isLoading: false, operation: 'none' });
    }
  }, [prevPageToken, searchQuery, resultsPerPage, currentPage, sortOrder, resultsCache]);

  const fetchChannelDetails = useCallback(async (channelId: string) => {
    setDetailsLoading(true);
    setError(null);

    try {
      const details = await getChannelDetailsById(channelId);
      setSelectedChannel(details);
    } catch (err) {
      logErrorWithContext('fetchChannelDetails', err, {
        query: channelId,
        searchType: 'fetchDetails',
      });
      setError(normalizeError(err));
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  const resetSearch = useCallback(() => {
    setSearchQuery("");
    setSearchResults([]);
    setSelectedChannel(null);
    setError(null);
    setPageToken(undefined);
    setPrevPageToken(undefined);
    setTotalResults(0);
    setCurrentPage(1);
    setPageTokens(new Map());
    setSortOrder("relevance");
    setResultsPerPage(20);
    setResultsCache(new Map());
  }, []);

  const nextPage = useCallback(async () => {
    if (!pageToken) return;

    setLoading(true);
    setLoadingState({ isLoading: true, operation: 'pagination', page: currentPage + 1 });
    setError(null);

    try {
      // Use backfill mechanism to ensure we get the target number of results
      const result = await fetchWithBackfill(
        searchQuery,
        resultsPerPage,
        pageToken,
        sortOrder
      );

      // Store the current page token in the map before moving forward
      const newPageTokens = new Map(pageTokens);
      newPageTokens.set(currentPage, pageToken);
      setPageTokens(newPageTokens);

      // Apply client-side sorting
      const sortedResults = applySortOrder(result.items, sortOrder);

      setSearchResults(sortedResults);
      setPageToken(result.nextPageToken);
      setPrevPageToken(result.prevPageToken);
      setCurrentPage((prev) => prev + 1);
      setSelectedChannel(null);
    } catch (err) {
      logErrorWithContext('nextPage', err, {
        query: searchQuery,
        pageToken,
        currentPage,
        sortOrder,
        resultsPerPage,
        searchType: 'nextPage',
      });
      setError(normalizeError(err));
    } finally {
      setLoading(false);
      setLoadingState({ isLoading: false, operation: 'none' });
    }
  }, [pageToken, searchQuery, resultsPerPage, pageTokens, currentPage, sortOrder]);

  const prevPage = useCallback(async () => {
    if (!prevPageToken) return;

    setLoading(true);
    setLoadingState({ isLoading: true, operation: 'pagination', page: currentPage - 1 });
    setError(null);

    try {
      // Use backfill mechanism to ensure we get the target number of results
      const result = await fetchWithBackfill(
        searchQuery,
        resultsPerPage,
        prevPageToken,
        sortOrder
      );

      // Apply client-side sorting
      const sortedResults = applySortOrder(result.items, sortOrder);

      setSearchResults(sortedResults);
      setPageToken(result.nextPageToken);
      setPrevPageToken(result.prevPageToken);
      setCurrentPage((prev) => Math.max(1, prev - 1));
      setSelectedChannel(null);
    } catch (err) {
      logErrorWithContext('prevPage', err, {
        query: searchQuery,
        pageToken: prevPageToken,
        currentPage,
        sortOrder,
        resultsPerPage,
        searchType: 'prevPage',
      });
      setError(normalizeError(err));
    } finally {
      setLoading(false);
      setLoadingState({ isLoading: false, operation: 'none' });
    }
  }, [prevPageToken, searchQuery, resultsPerPage, sortOrder]);

  const goToPage = useCallback(
    async (pageNum: number) => {
      if (pageNum < 1) return;
      if (pageNum === currentPage) return;

      const tokenForPage = pageTokens.get(pageNum);
      if (!tokenForPage && pageNum !== 1) return;

      setLoading(true);
      setLoadingState({ isLoading: true, operation: 'pagination', page: pageNum });
      setError(null);

      try {
        // Use backfill mechanism to ensure we get the target number of results
        const result = await fetchWithBackfill(
          searchQuery,
          resultsPerPage,
          tokenForPage,
          sortOrder
        );

        // Apply client-side sorting
        const sortedResults = applySortOrder(result.items, sortOrder);

        setSearchResults(sortedResults);
        setPageToken(result.nextPageToken);
        setPrevPageToken(result.prevPageToken);
        setCurrentPage(pageNum);
        setSelectedChannel(null);

        // Store the token for this page
        const newPageTokens = new Map(pageTokens);
        newPageTokens.set(pageNum, tokenForPage || "");
        setPageTokens(newPageTokens);
      } catch (err) {
        logErrorWithContext('goToPage', err, {
          query: searchQuery,
          pageToken: tokenForPage,
          currentPage: pageNum,
          sortOrder,
          resultsPerPage,
          searchType: 'goToPage',
        });
        setError(normalizeError(err));
      } finally {
        setLoading(false);
        setLoadingState({ isLoading: false, operation: 'none' });
      }
    },
    [currentPage, pageTokens, searchQuery, resultsPerPage, sortOrder]
  );

  const handleSetSortOrder = useCallback((order: YouTubeSortOrder) => {
    setLoadingState({ isLoading: true, operation: 'sort' });
    setSortOrder(order);
    setCurrentPage(1);
    setPageTokens(new Map());
    setPageToken(undefined);
    setPrevPageToken(undefined);
    setResultsCache(new Map());
    // Reset loading state after sort operation completes
    setTimeout(() => {
      setLoadingState({ isLoading: false, operation: 'none' });
    }, 0);
  }, []);

  const handleSetResultsPerPage = useCallback((count: number) => {
    setResultsPerPage(count);
    setCurrentPage(1);
    setPageTokens(new Map());
    setPageToken(undefined);
    setPrevPageToken(undefined);
    setResultsCache(new Map());
  }, []);

  // Reset pagination state when search query changes
  useEffect(() => {
    if (searchQuery.trim()) {
      setCurrentPage(1);
      setPageTokens(new Map());
      setPageToken(undefined);
      setPrevPageToken(undefined);
      setResultsCache(new Map());
    }
  }, [searchQuery]);

  // Persist search state to sessionStorage whenever key values change
  useEffect(() => {
    try {
      const stateToSave: SerializedSearchState = {
        query: searchQuery,
        sortOrder,
        currentPage,
        cache: serializeCache(resultsCache),
      };
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(stateToSave));
    } catch (err) {
      console.warn("Failed to save search state to sessionStorage:", err);
      // Silently fail - don't interrupt user interaction
    }
  }, [searchQuery, sortOrder, currentPage, resultsCache]);

  // Retry mechanism for transient errors
  const performRetry = useCallback(async () => {
    if (retryCount >= MAX_RETRIES || !lastOperationType) {
      return;
    }

    setPendingRetry(true);
    
    try {
      // Add exponential backoff delay before retry
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS * (retryCount + 1)));
      
      setRetryCount((prev) => prev + 1);
      
      // Retry based on the last operation type
      switch (lastOperationType) {
        case 'search':
          await performSearch();
          break;
        case 'nextPage':
          await nextPage();
          break;
        case 'prevPage':
          await prevPage();
          break;
        case 'goToPage':
          // goToPage requires the page number, so we'll retry at current page
          await goToPage(currentPage);
          break;
        case 'fetchDetails':
          // This would need additional context, skipping auto-retry for now
          break;
      }
    } finally {
      setPendingRetry(false);
    }
  }, [retryCount, lastOperationType, performSearch, nextPage, prevPage, goToPage, currentPage]);

  // Auto-retry for transient errors
  const lastErrorRef = useCallback((err: unknown) => {
    if (isTransientError(err) && retryCount < MAX_RETRIES) {
      // Store context for retry
      setLastOperationType(lastOperationType);
      // Auto-retry after delay
      void performRetry();
    }
  }, [retryCount, lastOperationType, performRetry]);

  return {
    searchQuery,
    setSearchQuery,
    searchResults,
    selectedChannel,
    setSelectedChannel,
    loadingState,
    loading,
    detailsLoading,
    error,
    pageToken,
    prevPageToken,
    totalResults,
    performSearch,
    loadNextPage,
    loadPrevPage,
    fetchChannelDetails,
    resetSearch,
    // Pagination state
    currentPage,
    pageTokens,
    sortOrder,
    resultsPerPage,
    resultsCache,
    // Pagination control functions
    nextPage,
    prevPage,
    goToPage,
    setSortOrder: handleSetSortOrder,
    setResultsPerPage: handleSetResultsPerPage,
    // Retry functionality
    retryCount,
    canRetry: retryCount < MAX_RETRIES && !!lastOperationType && !!error,
    performRetry,
  };
}
